/**
 * <tecton-header> — mega-menu + mobile drawer behaviour.
 *
 * Liquid port of NavigationClient.tsx. Behaviour matched to the React version:
 *   - hover opens a panel; leaving closes it after a 200ms grace period, so
 *     travelling diagonally from the trigger to the panel doesn't dismiss it
 *   - Escape and outside-click close
 *   - mobile drawer locks body scroll and traps focus, restoring focus to the
 *     hamburger on close
 *
 * Added for Shopify (the React nav had neither):
 *   - keyboard operation of the triggers, since they are real <button>s here
 *   - hover video play/pause on the mega-menu product cards
 */

const CLOSE_DELAY_FALLBACK = 200;

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class TectonHeader extends HTMLElement {
  connectedCallback() {
    this.closeDelay = Number(this.dataset.closeDelay) || CLOSE_DELAY_FALLBACK;
    this.closeTimer = null;
    this.openPanel = null;

    this.triggers = [...this.querySelectorAll('[data-panel-trigger]')];
    this.mega = this.querySelector('[data-mega]');
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.burger = this.querySelector('[data-drawer-toggle]');
    this.drawer = this.querySelector('#tecton-drawer');

    this.bindPanels();
    this.bindDrawer();
    this.bindHoverVideo();
    this.trackHeight();

    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      if (this.openPanel) this.closeNow();
      if (this.drawer?.hasAttribute('data-open')) this.toggleDrawer(false);
    };

    this.onPointerDown = (event) => {
      if (this.openPanel && !this.contains(event.target)) this.closeNow();
    };

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('mousedown', this.onPointerDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('mousedown', this.onPointerDown);
    document.removeEventListener('keydown', this.trapFocus, true);
    clearTimeout(this.closeTimer);
    this.heightObserver?.disconnect();
    document.body.style.overflow = '';
  }

  /* ── Height offset ───────────────────────────────────────────────────── */

  /**
   * The header is fixed, so it leaves no space in flow. Horizon's own offset
   * script bails out here — it looks for a `header-component` element, which
   * this section deliberately does not define — so publish the measured height
   * ourselves and let #header-group hold the space open.
   *
   * Measured rather than hardcoded so a taller logo or a wrapped menu cannot
   * silently push content under the bar.
   */
  trackHeight() {
    const bar = this.querySelector('.tecton-header__bar');
    if (!bar) return;

    const publish = () => {
      const height = Math.round(this.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--tecton-header-height', `${height}px`);
    };

    publish();
    this.heightObserver = new ResizeObserver(publish);
    this.heightObserver.observe(bar);
  }

  /* ── Mega panels ─────────────────────────────────────────────────────── */

  bindPanels() {
    if (!this.mega) return;

    for (const trigger of this.triggers) {
      const name = trigger.dataset.panelTrigger;

      trigger.addEventListener('mouseenter', () => this.open(name));
      trigger.addEventListener('mouseleave', () => this.scheduleClose());
      trigger.addEventListener('focus', () => this.open(name));

      // Pointer-less and touch users need an explicit toggle.
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.openPanel === name) this.closeNow();
        else this.open(name);
      });
    }

    this.mega.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
    this.mega.addEventListener('mouseleave', () => this.scheduleClose());

    // Tabbing out of the last item in a panel should close it.
    this.mega.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeNow();
    });
  }

  open(name) {
    clearTimeout(this.closeTimer);
    if (this.openPanel === name) return;

    this.openPanel = name;
    this.mega.hidden = false;
    // Force a frame so the max-height transition runs from its collapsed value
    // rather than being skipped by the hidden→visible switch.
    requestAnimationFrame(() => this.mega.setAttribute('data-open', ''));

    for (const panel of this.panels) {
      panel.hidden = panel.dataset.panel !== name;
    }
    for (const trigger of this.triggers) {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.panelTrigger === name));
    }
  }

  scheduleClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeNow(), this.closeDelay);
  }

  closeNow() {
    clearTimeout(this.closeTimer);
    if (!this.openPanel) return;

    this.openPanel = null;
    this.mega.removeAttribute('data-open');
    for (const trigger of this.triggers) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    // Keep the panel in the DOM until the collapse transition finishes.
    this.mega.addEventListener(
      'transitionend',
      () => {
        if (!this.openPanel) {
          this.mega.hidden = true;
          for (const panel of this.panels) panel.hidden = true;
        }
      },
      { once: true }
    );
  }

  /* ── Mobile drawer ───────────────────────────────────────────────────── */

  bindDrawer() {
    if (!this.burger || !this.drawer) return;

    this.trapFocus = (event) => {
      if (event.key !== 'Tab') return;
      const items = [...this.drawer.querySelectorAll(FOCUSABLE)];
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.drawer.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.drawer.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    this.burger.addEventListener('click', () => {
      this.toggleDrawer(!this.drawer.hasAttribute('data-open'));
    });
  }

  toggleDrawer(shouldOpen) {
    if (shouldOpen) {
      this.drawer.hidden = false;
      requestAnimationFrame(() => this.drawer.setAttribute('data-open', ''));
      this.burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.trapFocus, true);
      this.drawer.querySelector(FOCUSABLE)?.focus();
    } else {
      this.drawer.removeAttribute('data-open');
      this.burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.trapFocus, true);
      this.burger.focus();
      this.drawer.addEventListener(
        'transitionend',
        () => {
          if (!this.drawer.hasAttribute('data-open')) this.drawer.hidden = true;
        },
        { once: true }
      );
    }
  }

  /* ── Hover video on product cards ────────────────────────────────────── */

  bindHoverVideo() {
    for (const card of this.querySelectorAll('.tecton-nav-card')) {
      const video = card.querySelector('video');
      if (!video) continue;

      card.addEventListener('mouseenter', () => {
        video.currentTime = 0;
        video.play().catch(() => {
          /* autoplay refused — the still image is already showing */
        });
      });
      card.addEventListener('mouseleave', () => video.pause());
    }
  }
}

if (!customElements.get('tecton-header')) {
  customElements.define('tecton-header', TectonHeader);
}
